import { useQuery, useMutation } from './urqlHooks'

// Queries
const GET_REPOSITORY_SECTIONS = `
  query GET_REPOSITORY_SECTIONS {
    repositorySections {
      key
      label
      hasChildren
      children {
        key
        label
        singularLabel
        periodType
        fileLimit
        hasChildren
        children {
          key
          label
          singularLabel
          periodType
          fileLimit
          hasChildren
        }
      }
      singularLabel
      periodType
      fileLimit
    }
  }
`

const GET_REPOSITORY_PERIODS = `
  query GET_REPOSITORY_PERIODS($companyId: ID!, $sectionKey: String!, $year: Float, $month: Float) {
    repositoryPeriods(input: {
      companyId: $companyId
      sectionKey: $sectionKey
      year: $year
      month: $month
    }) {
      id
      companyId
      sectionKey
      periodKey
      periodLabel
      periodDate
      periodType
      status
      deliveryDate
      deliveredBy
      deliveredByEmail
      files {
        id
        officialName
        alias
        uploadDate
        uploadedBy
        uploadedByEmail
        status
        extension
        size
        url
        isPasswordProtected
      }
      history {
        id
        userId
        userEmail
        action
        date
        details
        fileId
        fileName
        fileAlias
        emailSubject
        emailBodyHtml
        notifiedEmails
      }
      innerComments {
        user {
          id
          name
          email
        }
        date
        comment
      }
      createdAt
      updatedAt
    }
  }
`

const GET_REPOSITORY_PERIOD = `
  query GET_REPOSITORY_PERIOD($periodId: ID!, $companyId: ID!) {
    repositoryPeriod(input: { periodId: $periodId, companyId: $companyId }, companyId: $companyId) {
      id
      companyId
      sectionKey
      periodKey
      periodLabel
      periodDate
      periodType
      status
      deliveryDate
      deliveredBy
      deliveredByEmail
      files {
        id
        officialName
        alias
        uploadDate
        uploadedBy
        uploadedByEmail
        status
        extension
        size
        url
        isPasswordProtected
      }
      history {
        id
        userId
        userEmail
        action
        date
        details
        fileId
        fileName
        fileAlias
        emailSubject
        emailBodyHtml
        notifiedEmails
      }
      innerComments {
        user {
          id
          name
          email
        }
        date
        comment
      }
      createdAt
      updatedAt
    }
  }
`

const GET_REPOSITORY_PERIOD_BY_KEY = `
  query GET_REPOSITORY_PERIOD_BY_KEY($companyId: ID!, $sectionKey: String!, $periodKey: String!) {
    repositoryPeriodByKey(companyId: $companyId, sectionKey: $sectionKey, periodKey: $periodKey) {
      id
      companyId
      sectionKey
      periodKey
      periodLabel
      periodDate
      periodType
      status
      deliveryDate
      deliveredBy
      deliveredByEmail
      files {
        id
        officialName
        alias
        uploadDate
        uploadedBy
        uploadedByEmail
        status
        extension
        size
        url
        isPasswordProtected
      }
      history {
        id
        userId
        userEmail
        action
        date
        details
        fileId
        fileName
        fileAlias
        emailSubject
        emailBodyHtml
        notifiedEmails
      }
      innerComments {
        user {
          id
          name
          email
        }
        date
        comment
      }
      createdAt
      updatedAt
    }
  }
`

const GET_REPOSITORY_PERIOD_HISTORY = `
  query GET_REPOSITORY_PERIOD_HISTORY($periodId: ID!, $companyId: ID!) {
    repositoryPeriodHistory(input: { periodId: $periodId, companyId: $companyId }, companyId: $companyId) {
      id
      userId
      userEmail
      action
      date
      details
      notifiedEmails
    }
  }
`

// Mutations
const UPLOAD_FILE_TO_PERIOD = `
  mutation UPLOAD_FILE_TO_PERIOD($companyId: ID!, $periodId: ID!, $file: RepositoryFileInput!, $replaceFileId: ID) {
    uploadFileToPeriod(input: {
      companyId: $companyId
      periodId: $periodId
      file: $file
      replaceFileId: $replaceFileId
    }) {
      id
      companyId
      sectionKey
      periodKey
      periodLabel
      periodDate
      periodType
      status
      deliveryDate
      deliveredBy
      deliveredByEmail
      files {
        id
        officialName
        alias
        uploadDate
        uploadedBy
        uploadedByEmail
        status
        extension
        size
        url
        isPasswordProtected
      }
      history {
        id
        userId
        userEmail
        action
        date
        details
        fileId
        fileName
        fileAlias
        emailSubject
        emailBodyHtml
        notifiedEmails
      }
      createdAt
      updatedAt
    }
  }
`

const NOTIFY_PERIOD = `
  mutation NOTIFY_PERIOD($companyId: ID!, $periodId: ID!, $emails: [String!]!) {
    notifyPeriod(input: {
      companyId: $companyId
      periodId: $periodId
      emails: $emails
    }) {
      id
      companyId
      sectionKey
      periodKey
      periodLabel
      periodDate
      periodType
      status
      deliveryDate
      deliveredBy
      deliveredByEmail
      files {
        id
        officialName
        alias
        uploadDate
        uploadedBy
        uploadedByEmail
        status
        extension
        size
        url
        isPasswordProtected
      }
      history {
        id
        userId
        userEmail
        action
        date
        details
        fileId
        fileName
        fileAlias
        emailSubject
        emailBodyHtml
        notifiedEmails
      }
      createdAt
      updatedAt
    }
  }
`

const DELETE_FILE_FROM_PERIOD = `
  mutation DELETE_FILE_FROM_PERIOD($companyId: ID!, $periodId: ID!, $fileId: ID!) {
    deleteFileFromPeriod(input: {
      companyId: $companyId
      periodId: $periodId
      fileId: $fileId
    }) {
      id
      companyId
      sectionKey
      periodKey
      periodLabel
      periodDate
      periodType
      status
      deliveryDate
      deliveredBy
      deliveredByEmail
      files {
        id
        officialName
        alias
        uploadDate
        uploadedBy
        uploadedByEmail
        status
        extension
        size
        url
        isPasswordProtected
      }
      history {
        id
        userId
        userEmail
        action
        date
        details
        fileId
        fileName
        fileAlias
        emailSubject
        emailBodyHtml
        notifiedEmails
      }
      createdAt
      updatedAt
    }
  }
`

const RECORD_FILE_DOWNLOAD = `
  mutation RECORD_FILE_DOWNLOAD($companyId: ID!, $periodId: ID!, $fileId: ID!) {
    recordFileDownload(input: {
      companyId: $companyId
      periodId: $periodId
      fileId: $fileId
    })
  }
`

const SEND_REPOSITORY_DOWNLOAD_OTP = `
  mutation SEND_REPOSITORY_DOWNLOAD_OTP(
    $companyId: ID!
    $periodId: ID!
    $fileId: ID!
  ) {
    sendRepositoryDownloadOtp(input: {
      companyId: $companyId
      periodId: $periodId
      fileId: $fileId
    }) {
      success
      emailMasked
      expiresInMinutes
    }
  }
`

const VERIFY_REPOSITORY_DOWNLOAD_OTP = `
  mutation VERIFY_REPOSITORY_DOWNLOAD_OTP(
    $companyId: ID!
    $periodId: ID!
    $fileId: ID!
    $code: String!
  ) {
    verifyRepositoryDownloadOtp(input: {
      companyId: $companyId
      periodId: $periodId
      fileId: $fileId
      code: $code
    })
  }
`

const GET_REPOSITORY_FILE_ACCESS_INFO = `
  query GET_REPOSITORY_FILE_ACCESS_INFO($accessToken: String!) {
    repositoryFileAccessInfo(accessToken: $accessToken) {
      fileName
      companyName
      periodLabel
      sectionLabel
      isPasswordProtected
      emailMasked
    }
  }
`

const SEND_REPOSITORY_DOWNLOAD_OTP_BY_ACCESS_TOKEN = `
  mutation SEND_REPOSITORY_DOWNLOAD_OTP_BY_ACCESS_TOKEN(
    $accessToken: String!
    $email: String
  ) {
    sendRepositoryDownloadOtpByAccessToken(input: {
      accessToken: $accessToken
      email: $email
    }) {
      success
      emailMasked
      expiresInMinutes
    }
  }
`

const VERIFY_REPOSITORY_DOWNLOAD_OTP_BY_ACCESS_TOKEN = `
  mutation VERIFY_REPOSITORY_DOWNLOAD_OTP_BY_ACCESS_TOKEN(
    $accessToken: String!
    $code: String!
    $email: String
  ) {
    verifyRepositoryDownloadOtpByAccessToken(input: {
      accessToken: $accessToken
      code: $code
      email: $email
    })
  }
`

const getPublicGraphqlUrl = () =>
  process.env.REACT_APP_API_URL || 'http://localhost:3000/graphql'

async function publicRepositoryGraphqlRequest(query, variables) {
  const response = await fetch(getPublicGraphqlUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  })
  const json = await response.json().catch(() => ({}))
  if (json?.errors?.length) {
    throw new Error(json.errors[0]?.message || 'Error de servidor')
  }
  return json?.data
}

export const fetchRepositoryFileAccessInfo = (accessToken) =>
  publicRepositoryGraphqlRequest(GET_REPOSITORY_FILE_ACCESS_INFO, { accessToken }).then(
    (data) => data?.repositoryFileAccessInfo,
  )

export const sendRepositoryDownloadOtpByAccessToken = (accessToken, email) =>
  publicRepositoryGraphqlRequest(SEND_REPOSITORY_DOWNLOAD_OTP_BY_ACCESS_TOKEN, {
    accessToken,
    email: email || null,
  }).then((data) => data?.sendRepositoryDownloadOtpByAccessToken)

export const verifyRepositoryDownloadOtpByAccessToken = (accessToken, code, email) =>
  publicRepositoryGraphqlRequest(VERIFY_REPOSITORY_DOWNLOAD_OTP_BY_ACCESS_TOKEN, {
    accessToken,
    code,
    email: email || null,
  }).then((data) => data?.verifyRepositoryDownloadOtpByAccessToken)

const CREATE_REPOSITORY_PERIOD_COMMENT = `
  mutation CREATE_REPOSITORY_PERIOD_COMMENT(
    $periodId: ID!
    $userId: String!
    $userName: String
    $userEmail: String
    $date: DateTime!
    $comment: String!
    $companyId: String!
  ) {
    createRepositoryPeriodComment(
      input: {
        periodId: $periodId
        user: {
          id: $userId
          name: $userName
          email: $userEmail
        }
        date: $date
        comment: $comment
      }
      companyId: $companyId
    ) {
      id
      innerComments {
        user {
          id
          name
          email
        }
        date
        comment
      }
    }
  }
`

const GET_UPLOAD_URL = `
  query GET_UPLOAD_URL($companyId: String!, $fileName: String!, $fileType: String!, $year: String!, $sectionKey: String!, $periodKey: String!) {
    getUploadUrl(
      companyId: $companyId
      fileName: $fileName
      fileType: $fileType
      year: $year
      sectionKey: $sectionKey
      periodKey: $periodKey
    ) {
      uploadUrl
      key
    }
  }
`

const REQUEST_FORM22_PDF = `
  mutation REQUEST_FORM22_PDF($companyId: String!, $year: Int) {
    requestForm22PDF(companyId: $companyId, year: $year) {
      status
      message
      data
    }
  }
`

// Hooks
export const GetRepositorySections = () => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_REPOSITORY_SECTIONS,
    requestPolicy: 'cache-first',
  })

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: () => {
      reexecuteQuery({ requestPolicy: 'network-only' })
    },
  }
}

export const GetRepositoryPeriods = ({ companyId, sectionKey, year, month, pause = false }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_REPOSITORY_PERIODS,
    variables: {
      companyId,
      sectionKey,
      // Only include year if it's provided and not undefined (for monthly sections)
      // For annual sections (year === undefined), omit year entirely so backend returns all years
      ...(year !== null && year !== undefined ? { year: parseFloat(year) } : {}),
      ...(month !== null && month !== undefined ? { month: parseFloat(month) } : {}),
    },
    pause: pause || !companyId || !sectionKey,
    requestPolicy: 'cache-and-network',
  })

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: () => {
      reexecuteQuery({ requestPolicy: 'network-only' })
    },
  }
}

export const GetRepositoryPeriod = ({ periodId }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_REPOSITORY_PERIOD,
    variables: {
      periodId,
    },
    pause: !periodId,
    requestPolicy: 'cache-first',
  })

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: () => {
      reexecuteQuery({ requestPolicy: 'network-only' })
    },
  }
}

export const GetRepositoryPeriodByKey = ({ companyId, sectionKey, periodKey }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_REPOSITORY_PERIOD_BY_KEY,
    variables: {
      companyId,
      sectionKey,
      periodKey,
    },
    pause: !companyId || !sectionKey || !periodKey,
    requestPolicy: 'cache-first',
  })

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: () => {
      reexecuteQuery({ requestPolicy: 'network-only' })
    },
  }
}

export const GetRepositoryPeriodHistory = ({ periodId }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_REPOSITORY_PERIOD_HISTORY,
    variables: {
      periodId,
    },
    pause: !periodId,
    requestPolicy: 'cache-first',
  })

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: () => {
      reexecuteQuery({ requestPolicy: 'network-only' })
    },
  }
}

export const UploadFileToPeriod = () => {
  const [result, executeMutation] = useMutation(UPLOAD_FILE_TO_PERIOD)

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => {
      return executeMutation(variables)
    },
  }
}

export const NotifyPeriod = () => {
  const [result, executeMutation] = useMutation(NOTIFY_PERIOD)

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => {
      return executeMutation(variables)
    },
  }
}

export const DeleteFileFromPeriod = () => {
  const [result, executeMutation] = useMutation(DELETE_FILE_FROM_PERIOD)

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => {
      return executeMutation(variables)
    },
  }
}

export const RecordFileDownload = () => {
  const [result, executeMutation] = useMutation(RECORD_FILE_DOWNLOAD)

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => {
      return executeMutation(variables)
    },
  }
}

export const SendRepositoryDownloadOtp = () => {
  const [result, executeMutation] = useMutation(SEND_REPOSITORY_DOWNLOAD_OTP)

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const VerifyRepositoryDownloadOtp = () => {
  const [result, executeMutation] = useMutation(VERIFY_REPOSITORY_DOWNLOAD_OTP)

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const CreateRepositoryPeriodComment = () => {
  const [result, executeMutation] = useMutation(CREATE_REPOSITORY_PERIOD_COMMENT)

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => {
      return executeMutation(variables)
    },
  }
}

export const GetUploadUrl = ({ companyId, fileName, fileType, year, sectionKey, periodKey, pause = true }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_UPLOAD_URL,
    variables: {
      companyId,
      fileName,
      fileType,
      year,
      sectionKey,
      periodKey,
    },
    pause: pause || !companyId || !fileName || !fileType || !year || !sectionKey || !periodKey,
    requestPolicy: 'network-only',
  })

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: () => {
      reexecuteQuery({ requestPolicy: 'network-only' })
    },
  }
}

const GET_REPOSITORY_EMAIL_PREVIEW = `
  query GET_REPOSITORY_EMAIL_PREVIEW($companyId: String!, $periodId: String!) {
    getRepositoryEmailPreview(companyId: $companyId, periodId: $periodId) {
      subject
      body
    }
  }
`

export const GetRepositoryEmailPreview = (companyId, periodId, pause = false) => {
  const [result] = useQuery({
    query: GET_REPOSITORY_EMAIL_PREVIEW,
    variables: { companyId, periodId },
    pause: pause || !companyId || !periodId,
    requestPolicy: 'network-only',
  })
  return result
}

export const RequestForm22PDF = () => {
  const [result, executeMutation] = useMutation(REQUEST_FORM22_PDF)

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => {
      return executeMutation(variables)
    },
  }
}

